import { useState } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceLine, Cell, LabelList,
} from "recharts";
import { fmt } from "@/lib/format";
import { totalExpenseKrw, netProfitKrw } from "@/lib/timeseries";
import ChartFrame from "@/components/ChartFrame";
import { useIsMobile } from "@/hooks/useMediaQuery";

// 관리자 대시보드 핵심 차트 — Stripe/Mixpanel 스타일 가시성 최적화 (2026-07-22)
// 핵심 원칙:
//   1. 막대 위 라벨 직접 표시 — 호버 없이 바로 수치 확인
//   2. 매출/지출을 반투명 배경 막대로 — 순이익과 시각적 비교
//   3. 범례는 차트 상단에 인라인으로 — 즉시 연결

// 막대 위 커스텀 라벨 — 짧게 compact 포맷으로
function BarLabel({ x, y, width, value, fill }) {
  if (value === 0 || value == null) return null;
  const isNeg = value < 0;
  return (
    <text
      x={x + width / 2}
      y={isNeg ? y + 14 : y - 5}
      textAnchor="middle"
      fontSize={10}
      fontWeight={600}
      fill={isNeg ? "#ef4444" : "#059669"}
    >
      {fmt.compact(Math.abs(value))}
    </text>
  );
}

export default function RevenueCostChart({ series }) {
  const [hidden, setHidden] = useState(new Set());
  const isMobile = useIsMobile();
  const [mobileMetric, setMobileMetric] = useState("profit"); // "profit" | "revenue" | "expense"
  const rows = series || [];

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-ink-200 bg-white p-8 text-center text-sm text-ink-400">
        데이터 없음
      </div>
    );
  }

  const data = [...rows].reverse().map((r) => ({
    bucket: r.bucketLabel || r.bucket,
    profit: netProfitKrw(r),
    revenue: r.revenueKrw || 0,
    expense: totalExpenseKrw(r),
  }));

  // ── 모바일: 1-series 만. 데스크톱 축소가 아니라 별도 composition(2026-09-21 Phase 1).
  //   기본 지표 = 직접비 기여이익. 매출/AI비용은 상단 토글로 전환. 막대 라벨 제거·축 최소화·높이 확보.
  if (isMobile) {
    const METRICS = [
      { id: "profit", label: "기여이익", color: "#10b981" },
      { id: "revenue", label: "매출", color: "#3b82f6" },
      { id: "expense", label: "지출", color: "#f97316" },
    ];
    const active = METRICS.find((m) => m.id === mobileMetric) || METRICS[0];
    return (
      <div className="rounded-xl border border-ink-100 bg-white p-3">
        <div className="mb-3 inline-flex rounded-lg border border-ink-200 bg-white p-0.5">
          {METRICS.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setMobileMetric(m.id)}
              className={`rounded-md px-3 py-1.5 text-[12px] font-semibold ${mobileMetric === m.id ? "bg-brand-600 text-white" : "text-ink-500"}`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#f0f0ee" />
              <XAxis dataKey="bucket" tick={{ fill: "#9ca3af", fontSize: 10 }} axisLine={false} tickLine={false}
                interval="preserveStartEnd" minTickGap={40} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 10 }} tickFormatter={fmt.compact} width={44} axisLine={false} tickLine={false} />
              <Tooltip
                cursor={{ fill: "rgba(0,0,0,0.04)" }}
                contentStyle={{ borderRadius: 10, border: "none", boxShadow: "0 8px 24px rgba(15,23,42,0.14)", fontSize: 12, padding: "10px 14px" }}
                formatter={(value) => [fmt.krw(value), active.label]}
                labelStyle={{ fontWeight: 700, marginBottom: 4, color: "#374151" }}
              />
              <ReferenceLine y={0} stroke="#d1d5db" strokeWidth={1} />
              {mobileMetric === "profit" ? (
                <Bar dataKey="profit" name="직접비 기여이익" maxBarSize={28} radius={[4, 4, 4, 4]}>
                  {data.map((entry, i) => (
                    <Cell key={i} fill={entry.profit >= 0 ? "#10b981" : "#ef4444"} />
                  ))}
                </Bar>
              ) : (
                <Line type="monotone" dataKey={mobileMetric} name={active.label} stroke={active.color} strokeWidth={2}
                  dot={false} activeDot={{ r: 4, fill: active.color }} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  function toggle(key) {
    setHidden((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  const LEGEND = [
    { key: "profit", label: "직접비 기여이익", type: "bar-dual" },
    { key: "revenue", label: "매출", color: "#3b82f6", type: "line" },
    { key: "expense", label: "지출", color: "#f97316", type: "line" },
  ];

  return (
    <div className="rounded-xl border border-ink-100 bg-white p-4">
      {/* 상단 인라인 범례 */}
      <div className="mb-3 flex items-center gap-4">
        {LEGEND.map((item) => {
          const isHidden = hidden.has(item.key);
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => toggle(item.key)}
              className={`flex items-center gap-1.5 text-[12px] font-medium transition-opacity ${isHidden ? "opacity-30" : "text-ink-700"}`}
            >
              {item.type === "bar-dual" ? (
                <span className="inline-flex gap-0.5">
                  <span className="inline-block h-3 w-2 rounded-sm bg-emerald-500" />
                  <span className="inline-block h-3 w-2 rounded-sm bg-red-500" />
                </span>
              ) : (
                <span className="inline-block h-0 w-4 border-t-2 rounded" style={{ borderColor: item.color }} />
              )}
              {item.label}
            </button>
          );
        })}
      </div>

      <ChartFrame base="h-56 sm:h-72" tall="h-96 sm:h-[32rem]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 24, right: 8, left: 0, bottom: 0 }} barGap={2}>
            <CartesianGrid vertical={false} stroke="#f0f0ee" />
            <XAxis
              dataKey="bucket"
              tick={{ fill: "#9ca3af", fontSize: 11 }}
              axisLine={false} tickLine={false} tickMargin={8}
              interval="preserveStartEnd" minTickGap={28}
            />
            <YAxis
              tick={{ fill: "#9ca3af", fontSize: 11 }}
              tickFormatter={fmt.compact}
              width={52} axisLine={false} tickLine={false}
            />
            <Tooltip
              cursor={{ fill: "rgba(0,0,0,0.04)" }}
              contentStyle={{
                borderRadius: 10, border: "none",
                boxShadow: "0 8px 24px rgba(15,23,42,0.14)", fontSize: 12,
                padding: "10px 14px",
              }}
              formatter={(value, name) => [fmt.krw(value), name]}
              labelStyle={{ fontWeight: 700, marginBottom: 4, color: "#374151" }}
            />
            <ReferenceLine y={0} stroke="#d1d5db" strokeWidth={1} />

            {/* 매출 — 실선 */}
            {!hidden.has("revenue") && (
              <Line
                type="monotone" dataKey="revenue" name="매출"
                stroke="#3b82f6" strokeWidth={2}
                dot={false} activeDot={{ r: 4, fill: "#3b82f6" }}
              />
            )}

            {/* 지출 — 실선 */}
            {!hidden.has("expense") && (
              <Line
                type="monotone" dataKey="expense" name="지출"
                stroke="#f97316" strokeWidth={2}
                dot={false} activeDot={{ r: 4, fill: "#f97316" }}
              />
            )}

            {/* 순이익 막대 — 양수 초록, 음수 빨강 + 상단 라벨 */}
            {!hidden.has("profit") && (
              <Bar dataKey="profit" name="직접비 기여이익" maxBarSize={32} radius={[4, 4, 4, 4]}>
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.profit >= 0 ? "#10b981" : "#ef4444"} />
                ))}
                <LabelList content={<BarLabel />} />
              </Bar>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </ChartFrame>
    </div>
  );
}
